import { useEffect } from 'react'
import { useTextStream } from '@/hooks/useTextStream'
import type { InterviewPhase, InterviewStatus } from '@/types/interview'
import { SHOW_CANDIDATE_TRANSCRIPT } from '@/config/features'

interface ControlBarProps {
  status: InterviewStatus
  phase: InterviewPhase
  agentMessage: string
  realtimeTranscript: string
  recordingElapsed: number
  onStart: () => void
  onNext: () => void
  onEnd: () => void
  startLabel?: string
  nextLabel?: string
  endLabel?: string
}

function formatTime(secs: number): string {
  const m = Math.floor(secs / 60)
  const s = secs % 60
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

export function ControlBar({
  status,
  phase,
  realtimeTranscript,
  recordingElapsed,
  onStart,
  onNext,
  onEnd,
  startLabel = 'Interview starten',
  nextLabel = 'Weiter',
  endLabel = 'Beenden',
}: ControlBarProps) {
  const { displayed, complete } = useTextStream()

  // Complete text reveal instantly when avatar stops speaking (matches candidate.js completeTextReveal)
  useEffect(() => {
    if (phase !== 'avatar_speaking') complete()
  }, [phase]) // eslint-disable-line react-hooks/exhaustive-deps

  const isReady = status === 'ready'
  const isRunning = status === 'running'

  // Single action button: disabled while setup/speaking, enabled when ready or user_response
  const actionLabel = isRunning ? nextLabel : startLabel
  const actionEnabled = isReady || (isRunning && phase === 'user_response')

  function handleAction() {
    if (isReady) onStart()
    else if (isRunning && phase === 'user_response') onNext()
  }

  return (
    <div className="interview-controls-bar">
      {/* Agent text area */}
      <div className="agent-speaking-area">
        <div className={`agent-text-container${phase === 'avatar_speaking' ? ' agent-speaking' : ''}`} id="agent-text-container">
          <div className="agent-message" id="agent-message">
            {displayed}
            {SHOW_CANDIDATE_TRANSCRIPT &&
              realtimeTranscript &&
              isRunning &&
              phase === 'user_response' && (
                <span style={{ color: '#8ab4f8', marginLeft: 8, fontStyle: 'italic' }}>
                  {realtimeTranscript}
                </span>
              )}
          </div>
        </div>
      </div>

      {/* Control buttons */}
      <div className="control-buttons">
        {/* Recording timer */}
        {isRunning && (
          <div id="recording-indicator" style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#DC3545', fontSize: '0.85rem', fontFamily: 'monospace' }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#DC3545', animation: 'pulse 1.5s infinite' }} />
            <span id="recording-timer">{formatTime(recordingElapsed)}</span>
          </div>
        )}

        {/* Single action button: "Interview starten" → "Weiter" */}
        <button
          id="action-btn"
          className="control-btn action-btn-custom"
          disabled={!actionEnabled}
          onClick={handleAction}
        >
          <span id="action-btn-text">{actionLabel}</span>
        </button>

        {/* End button — visible once running */}
        {isRunning && (
          <button id="close" className="control-btn end-btn" onClick={onEnd}>
            <div className="btn-icon">📞</div>
            <span>{endLabel}</span>
          </button>
        )}
      </div>
    </div>
  )
}
